/**
 * AdminLayout - Wrapper pour toutes les pages admin
 * Inclut le sidebar, le header et le toggle thème clair/sombre
 */
import React, { useState } from 'react';
import { AdminSidebar, AdminSidebarToggle } from './AdminSidebar';
import { useAdminTheme, adminThemeClasses } from '../../contexts/AdminThemeContext';
import { Sun, Moon } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export function AdminLayout({ children, title, subtitle }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { theme, toggleTheme, isLight } = useAdminTheme();
  const t = adminThemeClasses[theme];

  return (
    <div className={cn(
      "min-h-screen transition-colors duration-200",
      isLight ? "bg-gray-100 admin-light-theme" : "bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950"
    )}>
      {/* Sidebar */}
      <AdminSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(false)} />
      
      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : ''}`}>
        {/* Header */}
        <header className={cn(
          "border-b backdrop-blur-sm sticky top-0 z-40 transition-colors duration-200",
          isLight 
            ? "border-gray-200 bg-white/90" 
            : "border-zinc-800 bg-zinc-900/50"
        )}>
          <div className="px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <AdminSidebarToggle onClick={() => setSidebarOpen(true)} />
              {title && (
                <div>
                  <h1 className={cn(
                    "text-xl font-bold",
                    isLight ? "text-gray-900" : "text-white"
                  )}>{title}</h1>
                  {subtitle && (
                    <p className={cn(
                      "text-sm",
                      isLight ? "text-gray-600" : "text-gray-400"
                    )}>{subtitle}</p>
                  )}
                </div>
              )}
            </div>
            
            {/* Theme Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={toggleTheme}
              className={cn(
                "gap-2 transition-colors",
                isLight 
                  ? "border-gray-300 bg-white hover:bg-gray-100 text-gray-700" 
                  : "border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-gray-300"
              )}
              data-testid="theme-toggle"
            >
              {isLight ? (
                <>
                  <Moon className="w-4 h-4" />
                  <span className="hidden sm:inline">Sombre</span>
                </>
              ) : (
                <>
                  <Sun className="w-4 h-4" />
                  <span className="hidden sm:inline">Clair</span>
                </>
              )}
            </Button>
          </div>
        </header>
        
        {/* Page Content */}
        <main className="p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;
